import './styles.css';

export type IconSize = 'sm' | 'base' | 'lg';

interface GlyphProps {
  size?: IconSize;
  className?: string;
}

function glyphClassName(size: IconSize = 'base', className?: string) {
  return `icon icon--${size}${className ? ` ${className}` : ''}`;
}

/* ------------------------------------------------------------------
   底部 Tab 导航图标（原 App.tsx NavGlyph，active 时填充当前色）
------------------------------------------------------------------ */
export type NavGlyphKind = 'home' | 'customers' | 'search' | 'profile';

export function NavGlyph({
  kind,
  active = false,
  size = 'base',
  className
}: GlyphProps & { kind: NavGlyphKind; active?: boolean }) {
  const cls = glyphClassName(size, className);
  if (kind === 'home') {
    return (
      <svg className={cls} viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M3.7 10.7 12 3.8l8.3 6.9v8.7a1.8 1.8 0 0 1-1.8 1.8h-13a1.8 1.8 0 0 1-1.8-1.8z"
          fill={active ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path d="M9.2 21.2v-6.1h5.6v6.1" fill={active ? '#fff' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === 'customers') {
    return (
      <svg className={cls} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="7.8" r="3.35" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" />
        <path d="M5.3 20c.5-4.2 2.9-6.3 6.7-6.3s6.2 2.1 6.7 6.3z" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === 'search') {
    return (
      <svg className={cls} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="10.7" cy="10.7" r="6.4" fill="none" stroke="currentColor" strokeWidth="1.9" />
        <path d="m15.5 15.5 4.2 4.2" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg className={cls} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="7.4" r="3.3" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" />
      <path d="M5.2 20c.4-4.3 2.8-6.5 6.8-6.5s6.4 2.2 6.8 6.5z" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

/* 村头像内的房子图标（原 Search 页 HouseGlyph） */
export function HouseGlyph({ size = 'base', className }: GlyphProps) {
  return (
    <svg
      className={glyphClassName(size, className)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3.2 10.2 12 3l8.8 7.2" />
      <path d="M5.4 9.4V21h13.2V9.4M9.4 21v-7h5.2v7" />
    </svg>
  );
}

/* 设置页：酒瓶（原 BottleOutlineIcon） */
export function BottleIcon({ size = 'base', className }: GlyphProps) {
  return (
    <svg
      className={glyphClassName(size, className)}
      viewBox="0 0 28 28"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 3.5h6v4.2l2.5 3.4v12.4h-11V11.1L11 7.7z" />
      <path d="M8.5 14h11M12.2 3.5v4.2M11.2 18.2h5.6" />
    </svg>
  );
}

/* 设置页：退出登录（原 LogoutOutlineIcon） */
export function LogoutIcon({ size = 'base', className }: GlyphProps) {
  return (
    <svg
      className={glyphClassName(size, className)}
      viewBox="0 0 28 28"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12.5 4.5h-6v19h6M16.5 9l5 5-5 5M9.5 14h12" />
    </svg>
  );
}

/* 设置页：主题模式月亮（原 MoonOutlineIcon） */
export function MoonIcon({ size = 'base', className }: GlyphProps) {
  return (
    <svg
      className={glyphClassName(size, className)}
      viewBox="0 0 28 28"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20.8 18.6A9.2 9.2 0 0 1 9.4 7.2a9.3 9.3 0 1 0 11.4 11.4Z" />
    </svg>
  );
}

/* 价格档位标签（原 PriceLevelManage 页 PriceTagIcon） */
export function PriceTagIcon({ size = 'base', className }: GlyphProps) {
  return (
    <svg
      className={glyphClassName(size, className)}
      viewBox="0 0 48 48"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6.7 25.8 25.1 7.4h12.4a3 3 0 0 1 3 3v12.4L22.1 41.2a3.2 3.2 0 0 1-4.5 0L6.7 30.3a3.2 3.2 0 0 1 0-4.5Z" />
      <circle cx="33.1" cy="14.8" r="3.1" />
    </svg>
  );
}

/* 列表排序拖动手柄 */
export function DragHandleIcon({ size = 'base', className }: GlyphProps) {
  return (
    <svg
      className={glyphClassName(size, className)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M5 8h14M5 12h14M5 16h14" />
    </svg>
  );
}

/* 登录页：用户名（原 Login 页 UserIcon） */
export function UserIcon({ size = 'base', className }: GlyphProps) {
  return (
    <svg
      className={glyphClassName(size, className)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.55"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="7.5" r="3.6" />
      <path d="M5.2 20c.45-4.05 2.65-6.1 6.8-6.1s6.35 2.05 6.8 6.1" />
    </svg>
  );
}

/* 登录页：密码锁（原 Login 页 LockIcon） */
export function LockIcon({ size = 'base', className }: GlyphProps) {
  return (
    <svg
      className={glyphClassName(size, className)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.55"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="5.3" y="10.2" width="13.4" height="10" rx="2" />
      <path d="M8.3 10.2V7.4a3.7 3.7 0 0 1 7.4 0v2.8" />
      <path d="M12 14.4v2.2" />
    </svg>
  );
}
